import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import { BarChart3, Save, Loader, ExternalLink } from 'lucide-react';

const API_URL = process.env.REACT_APP_API_URL || '/api';

/**
 * TrackingScriptsEditor — admin UI for pasting in GA4 / Meta Pixel /
 * Google Search Console verification IDs. The values feed
 * `<TrackingScripts />` which injects the appropriate <script> /
 * <meta> tags on the public-facing pages.
 *
 * Storage: piggy-backs on `system_settings` via /api/tracking-settings.
 * No new table.
 */

const FIELDS = [
  {
    key: 'tracking_ga4_id',
    label: 'Google Analytics 4 — Measurement ID',
    placeholder: 'G-XXXXXXXXXX',
    helpText: 'Find this in GA4 → Admin → Data Streams → your stream. Format: G-XXXXXXXXXX. Pasting the full <script> tag also works — we extract the ID.',
    helpUrl: 'https://support.google.com/analytics/answer/9539598',
  },
  {
    key: 'tracking_meta_pixel_id',
    label: 'Meta (Facebook) Pixel ID',
    placeholder: '1234567890123456',
    helpText: 'Find this in Meta Events Manager → Data Sources → your pixel. Numeric ID, 6–20 digits. Required for Facebook / Instagram ad tracking.',
    helpUrl: 'https://www.facebook.com/business/help/952192354843755',
  },
  {
    key: 'tracking_gsc_verification',
    label: 'Google Search Console — Verification Tag',
    placeholder: 'abc123XYZ_full-token-from-google',
    helpText: 'Copy only the content="..." value, OR paste the entire <meta> tag — we extract the content. Required for SEO-grade Search Console verification.',
    helpUrl: 'https://support.google.com/webmasters/answer/9008080',
  },
];

const TrackingScriptsEditor = () => {
  const [values, setValues] = useState({
    tracking_ga4_id: '',
    tracking_meta_pixel_id: '',
    tracking_gsc_verification: '',
  });
  const [original, setOriginal] = useState(values);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    axios.get(`${API_URL}/tracking-settings`)
      .then((res) => {
        const next = {
          tracking_ga4_id: res.data?.tracking_ga4_id || '',
          tracking_meta_pixel_id: res.data?.tracking_meta_pixel_id || '',
          tracking_gsc_verification: res.data?.tracking_gsc_verification || '',
        };
        setValues(next);
        setOriginal(next);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const dirty = JSON.stringify(values) !== JSON.stringify(original);

  const handleSave = async () => {
    setSaving(true);
    try {
      const token = sessionStorage.getItem('token');
      await axios.post(`${API_URL}/tracking-settings`, values, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setOriginal(values);
      toast.success('Tracking settings saved. Reload public pages to see new tags inject.');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to save tracking settings');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="card">
        <div className="flex items-center text-gray-500 py-6 justify-center">
          <Loader size={18} className="animate-spin mr-2" />
          Loading tracking settings...
        </div>
      </div>
    );
  }

  return (
    <div className="card">
      <div className="flex items-center space-x-3 mb-4">
        <BarChart3 className="text-primary-600" size={24} />
        <h3 className="text-xl font-bold text-gray-900">Third-Party Tracking</h3>
      </div>
      <p className="text-sm text-gray-600 mb-4">
        Optional. If you use Google Analytics 4, Meta Pixel, or Google Search Console,
        paste the relevant IDs below. Tags inject on your public booking page on next reload.
        Leave blank to skip — the in-house Traffic Analytics dashboard (Stats page) works
        either way and never depends on these.
      </p>

      <div className="space-y-4">
        {FIELDS.map((f) => (
          <div key={f.key}>
            <label className="block text-sm font-semibold text-gray-700 mb-1">
              {f.label}
              <a
                href={f.helpUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="ml-2 inline-flex items-center text-xs text-primary-600 hover:text-primary-700 font-normal"
              >
                Where to find this <ExternalLink size={11} className="ml-0.5" />
              </a>
            </label>
            <input
              type="text"
              value={values[f.key]}
              onChange={(e) => setValues((v) => ({ ...v, [f.key]: e.target.value }))}
              placeholder={f.placeholder}
              className="input font-mono text-sm w-full"
            />
            <p className="text-xs text-gray-500 mt-1">{f.helpText}</p>
          </div>
        ))}
      </div>

      <div className="mt-6 flex items-center justify-between">
        <p className="text-xs text-gray-500">
          Privacy note: when you enable a third-party tracker, that vendor's terms apply
          to your visitors. Configure your privacy policy accordingly.
        </p>
        <button
          onClick={handleSave}
          disabled={saving || !dirty}
          className={`inline-flex items-center px-4 py-2 text-sm rounded ${
            dirty
              ? 'bg-primary-600 text-white hover:bg-primary-700'
              : 'bg-gray-200 text-gray-500 cursor-not-allowed'
          }`}
        >
          {saving ? <Loader size={14} className="animate-spin mr-1" /> : <Save size={14} className="mr-1" />}
          {saving ? 'Saving...' : dirty ? 'Save tracking settings' : 'Saved'}
        </button>
      </div>
    </div>
  );
};

export default TrackingScriptsEditor;
